import { validate } from "class-validator";
import { useTranslation } from "../../hooks";
const requiredFieldConstraints = [
    "isNotEmpty",
    "IsCalendarShiftRequiredConstraint",
];
const constraintOverrides = [
    ...requiredFieldConstraints.map((constraintKey) => ({
        constraintKey,
        translationKey: "shared.error_messages:validation.required_field",
        defaultTranslation: "This field is required",
    })),
    {
        constraintKey: "IsStartDateBeforeEndDateConstraint",
        translationKey: "shared.error_messages:validation.invalid_date_range",
        defaultTranslation: "Start date must be before end date",
    },
    {
        constraintKey: "IsAvailabilityShiftRequiredConstraint",
        translationKey: "module.availability:validation.shift_hours_required",
        defaultTranslation: "Shift is required when hours are greater than 0",
    },
];
const constraintOverridesByKey = {};
constraintOverrides.forEach((override) => (constraintOverridesByKey[override.constraintKey] = override));
export function useValidation() {
    const { t } = useTranslation();
    const getFailedConstraints = (error) => {
        let failedConstraints = [];
        if (error.constraints) {
            failedConstraints = Object.entries(error.constraints).map(([constraint, message]) => {
                return { constraint, message };
            });
        }
        return failedConstraints;
    };
    const validateFields = async (object) => {
        const errors = await validate(object, {
            forbidUnknownValues: false,
        });
        return getValidationErrorsFromValidatedObject(errors);
    };
    const translateConstraints = (error) => {
        const getMessageKey = (message) => message.split("?")[0];
        const getMessageParams = (message) => {
            let messageParams = {};
            const paramStr = message.split("?")[1];
            if (paramStr) {
                const valuePairs = paramStr.split("&");
                valuePairs.forEach((pair) => {
                    const [key, value] = pair.split("=");
                    messageParams[key] = value;
                });
            }
            return messageParams;
        };
        const constraints = error.constraints || {};
        Object.keys(constraints).forEach((propName) => {
            const message = constraints[propName];
            const messageKey = getMessageKey(message);
            const messageParams = getMessageParams(message);
            constraints[propName] = t(messageKey, messageParams);
        });
    };
    const getValidationErrorsFromValidatedObject = (errors) => {
        const validationErrors = {
            fields: {},
            list: [],
        };
        errors.forEach((error) => {
            var _a;
            translateConstraints(error);
            validationErrors.fields[error.property] = {
                validation: error,
                errors: getFailedConstraints(error),
            };
            validationErrors.list.push(error);
            (_a = error.children) === null || _a === void 0 ? void 0 : _a.forEach((child, index) => {
                let childrenByIndex;
                childrenByIndex =
                    validationErrors.fields[error.property].childrenByIndex || {};
                validationErrors.fields[error.property].childrenByIndex =
                    childrenByIndex;
                childrenByIndex[child.property] =
                    getValidationErrorsFromValidatedObject(child.children);
            });
        });
        return validationErrors;
    };
    const formatConstraintMessage = (constraintKey, message) => {
        const override = constraintOverridesByKey[constraintKey];
        if (override) {
            const { translationKey: key, defaultTranslation: defaultValue } = override;
            message = t(key, { defaultValue });
        }
        return message;
    };
    const getFieldValidationMessage = (fieldValidationErrors) => {
        let validationMessage = "";
        const errors = fieldValidationErrors === null || fieldValidationErrors === void 0 ? void 0 : fieldValidationErrors.errors;
        if (errors && errors.length > 0) {
            let validation;
            validation = errors.find((error) => error.constraint === "isNotEmpty");
            if (!validation) {
                validation = errors[0];
            }
            const { constraint, message } = validation;
            validationMessage = formatConstraintMessage(constraint, message);
        }
        return validationMessage;
    };
    const validateProperty = async (dto, property, validationErrors) => {
        const errors = await validateFields(dto);
        const newErrors = Object.assign({}, validationErrors);
        const index = newErrors.list.findIndex((error) => error.property === property);
        if (errors.fields[property]) {
            newErrors.fields[property] = errors.fields[property];
            if (index !== -1) {
                newErrors.list.splice(index, 1, errors.fields[property].validation);
            }
            else {
                newErrors.list.push(errors.fields[property].validation);
            }
        }
        else {
            delete newErrors.fields[property];
            newErrors.list.splice(index, 1);
        }
        return newErrors;
    };
    const validateArrayElement = async (props) => {
        var _a, _b, _c, _d, _e, _f;
        const { validator, arrayProp, arrayIndex, form } = props;
        const { setValidationErrors, validationErrors } = form;
        const newErrors = JSON.parse(JSON.stringify(validationErrors));
        const errors = await validateProperty(validator, arrayProp, validationErrors);
        const fieldErrors = errors.fields[arrayProp];
        if (fieldErrors) {
            let newFieldErrors = newErrors.fields[arrayProp];
            if (!newFieldErrors) {
                newFieldErrors = {
                    childrenByIndex: {},
                    errors: [],
                    validation: {
                        property: arrayProp,
                        children: [],
                        value: {},
                        target: {},
                    },
                };
                newErrors.fields[arrayProp] = newFieldErrors;
            }
            // -----------------------------------------------------------------------------------------
            // Update fields[arrayProp].childrenByIndex
            // -----------------------------------------------------------------------------------------
            const arrayElementErrors = (_a = fieldErrors.childrenByIndex) === null || _a === void 0 ? void 0 : _a[arrayIndex];
            if (arrayElementErrors) {
                let newChildrenByIndex = newFieldErrors.childrenByIndex;
                newChildrenByIndex[arrayIndex] = arrayElementErrors;
            }
            else {
                // no errors, remove from new state
                (_c = (_b = newErrors.fields[arrayProp]) === null || _b === void 0 ? void 0 : _b.childrenByIndex) === null || _c === void 0 ? true : delete _c[arrayIndex];
            }
            // -----------------------------------------------------------------------------------------
            // Update fields[arrayProp].validation
            // -----------------------------------------------------------------------------------------
            let newValidationChildren = newFieldErrors.validation.children;
            const newValidationChildIndex = newValidationChildren.findIndex((child) => {
                return child.property === String(arrayIndex);
            });
            const childValidationError = (_e = (_d = fieldErrors.validation) === null || _d === void 0 ? void 0 : _d.children) === null || _e === void 0 ? void 0 : _e.find((child) => {
                return child.property === String(arrayIndex);
            });
            if (childValidationError) {
                // Update fields[arrayProp].validation.children
                if (newValidationChildIndex === -1) {
                    newValidationChildren.push();
                }
                else {
                    newValidationChildren.splice(newValidationChildIndex, 1, childValidationError);
                }
            }
            else {
                // no error, remove from new state
                if (newValidationChildIndex !== -1) {
                    newValidationChildren.splice(newValidationChildIndex, 1);
                }
            }
        }
        else {
            // no error, remove from state
            delete newErrors.fields[arrayProp];
        }
        const listError = errors.list.find((error) => {
            return error.property === arrayProp;
        });
        if (listError) {
            // -----------------------------------------------------------------------------------------
            // Update list[].children
            // -----------------------------------------------------------------------------------------
            const arrayElementError = (_f = listError.children) === null || _f === void 0 ? void 0 : _f.find((error) => {
                return error.property === String(arrayIndex);
            });
            const newListError = newErrors.list.find((error) => {
                return error.property === arrayProp;
            });
            const newListErrorChildren = (newListError === null || newListError === void 0 ? void 0 : newListError.children) || [];
            const newListErrorIndex = newListErrorChildren.findIndex((error) => {
                return error.property === String(arrayIndex);
            });
            if (arrayElementError) {
                if (newListError) {
                    if (newListErrorIndex === -1) {
                        newListErrorChildren.push(arrayElementError);
                    }
                    else {
                        newListErrorChildren.splice(newListErrorIndex, 1, arrayElementError);
                    }
                }
            }
            else {
                // no error, remove from new state
                if (newListError && newListErrorIndex !== -1) {
                    newListErrorChildren.splice(newListErrorIndex, 1);
                }
            }
        }
        else {
            // no error, remove from state
            const newListIndex = newErrors.list.findIndex((error) => {
                return error.property === arrayProp;
            });
            if (newListIndex !== -1) {
                newErrors.list.splice(newListIndex, 1);
            }
        }
        setValidationErrors(newErrors);
    };
    return {
        validateFields,
        validateProperty,
        validateArrayElement,
        getFieldValidationMessage,
    };
}
